// src/pages/admin/TrialManager.js
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../firebase';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  runTransaction,
} from 'firebase/firestore';
import {
  ArrowLeft,
  Plus,
  Edit2,
  Trash2,
  X,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Clock,
  Calendar,
  Phone,
  Mail,
  Link as LinkIcon,
  Unlink,
  Ban,
  Award,
  RotateCcw,
  Info,
  Users,
  DollarSign,
} from 'lucide-react';
import {
  isoToDisplayDate,
  isoToDisplayTime,
  formatISOForDisplay,
} from '../../utils/timezoneUtils';
import { resolveTimezone } from '../../config/locationTimezones';
import { calculateAge } from '../../utils/childrenHelper';
import ProfileContactFields from '../../components/ProfileContactFields';
import {
  TRIAL_STATUSES,
  TRIAL_STATUS_LABELS,
  DEFAULT_TRIAL_DURATION_MINS,
  TRIAL_DIALECTS,
  HEARD_ABOUT_OPTIONS,
  getAllowedTransitions,
  requiresOutcomeReason,
  appendTrialStatusHistory,
  getTrialTimezone,
  computeTrialFunnelStats,
} from '../../utils/trialHelper';

// ─── Constants ────────────────────────────────────────────────────────────────

const TRIAL_STATUS_STYLES = {
  requested: 'bg-gray-100 text-gray-700',
  scheduled: 'bg-blue-100 text-blue-700',
  held:      'bg-purple-100 text-purple-700',
  no_show:   'bg-orange-100 text-orange-700',
  cancelled: 'bg-gray-200 text-gray-600',
  won:       'bg-green-100 text-green-700',
  lost:      'bg-red-100 text-red-700',
  expired:   'bg-gray-200 text-gray-500',
};

const FUNNEL_TILES = ['requested', 'scheduled', 'held', 'no_show', 'won', 'lost'];

// PHASE 2 — conversion fields to fold into a status-change update.
//
// convertedPackageId is written ONLY by the payment webhook (createTrialSignup,
// admin trial-create and every TrialManager status handler leave it null). It is
// the record that money changed hands. An admin action must NEVER clear it —
// checked first, returns early. The child keeps their course; the trial must
// keep the link to the payment that bought it (Phase 3's revenue-per-code report
// reads it).
//
// convertedAt on its own (no convertedPackageId) is a hand-mark — set by
// handleOutcomeConfirm on a `won` outcome. It's sticky: nothing clears it when
// the trial is later corrected (typically via Override, the only path off a
// terminal `won`). A stale one reads as a conversion that never happened —
// Phase 3's time-to-conversion metric keys off convertedAt — so drop it whenever
// a status change lands anywhere other than `won`.
function conversionFieldsForStatusChange(trial, newStatus) {
  // convertedPackageId is written ONLY by the payment webhook — the record that
  // money changed hands. Sacred: no admin action clears it. Checked first.
  if (trial && trial.convertedPackageId) return {};
  // `newStatus === 'won'` is the caller's job (handleOutcomeConfirm sets a fresh
  // convertedAt for a deliberate hand-mark); don't fight it.
  if (newStatus === 'won') return {};
  // A payment-less `won` being corrected, or a stale hand-mark timestamp still
  // on an already-corrected trial. No money behind it, not landing on `won` —
  // clear it so it stops reading as a conversion (Phase 3 time-to-conversion).
  if (trial && (trial.status === 'won' || trial.convertedAt)) {
    return { convertedAt: null };
  }
  return {};
}

// Parent-level fields, entered once and shared by every sibling created in the
// same submission. Per-student fields — name, DOB, Arabic level, dialect, AND
// (per row now) teacher/schedule/duration/Zoom — live in the `students` array;
// see makeEmptyStudentRow.
const EMPTY_TRIAL_FORM = {
  isAdult: false,
  parentName: '',
  parentEmail: '',
  parentPhone: '',
  attribution: { code: '', heardAbout: '', heardAboutOther: '' },
  intake: { city: '', stateProvince: '', country: '', preferredTimes: '', notes: '' },
};

// copyFrom (row 0) seeds a new row's own schedule fields as a starting
// snapshot for if/when its "same as first" checkbox gets unticked.
function makeEmptyStudentRow(copyFrom = null) {
  return {
    key: `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, // local React key only, never persisted
    studentName: '',
    dateOfBirth: '',
    arabicLevel: '',
    dialect: '',
    sameAsFirst: true, // meaningful only for rows after the first
    teacherId: copyFrom?.teacherId ?? '',
    dateStr: copyFrom?.dateStr ?? '',
    timeStr: copyFrom?.timeStr ?? '',
    durationMins: copyFrom?.durationMins ?? DEFAULT_TRIAL_DURATION_MINS,
    meetingUrl: copyFrom?.meetingUrl ?? '',
  };
}

// ─── Local timezone conversion ─────────────────────────────────────────────
// timezoneUtils' exported helpers convert recurring weekday+time <-> UTC.
// A trial's scheduledAt is a one-off calendar datetime, which needs a
// different conversion (specific Y-M-D + H:M in an arbitrary IANA zone ->
// UTC instant). Kept local to this page since no other admin page needs it.

function getZoneOffsetMinutes(timezone, utcDate) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(utcDate);
    const map = Object.fromEntries(parts.map(p => [p.type, p.value]));
    const hour = map.hour === '24' ? 0 : Number(map.hour);
    const localAsUTC = Date.UTC(Number(map.year), Number(map.month) - 1, Number(map.day), hour, Number(map.minute), 0);
    return (localAsUTC - utcDate.getTime()) / 60000;
  } catch {
    return 0;
  }
}

// Two-pass offset resampling (same technique timezoneUtils uses internally for
// utcRangeForCalendarDay) so DST transition days convert correctly.
function localDateTimeToUTCISO(dateStr, timeStr, timezone) {
  if (!dateStr || !timeStr || !timezone) return null;
  try {
    const [y, mo, d] = dateStr.split('-').map(Number);
    const [h, mi] = timeStr.split(':').map(Number);
    const naiveUTC = Date.UTC(y, mo - 1, d, h, mi, 0);
    const offset1 = getZoneOffsetMinutes(timezone, new Date(naiveUTC));
    const pass1 = naiveUTC - offset1 * 60000;
    const offset2 = getZoneOffsetMinutes(timezone, new Date(pass1));
    return new Date(naiveUTC - offset2 * 60000).toISOString();
  } catch {
    return null;
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDateShort(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '—';
  }
}

// Deliberately DIVERGES from CreateAccount.js/AddChildren.js, which serialize
// their react-datepicker's LOCAL-midnight Date via toISOString() — fine there
// because it's one parent entering their own child once. A trial is shared
// admin data, read back by staff in different countries, and a birth date has
// no time-of-day component to begin with — so unlike scheduledAt (a real
// instant, correctly zoned), DOB must never pass through anyone's local
// midnight. Date.UTC(y, m-1, d) anchors it at UTC midnight instead, so
// .slice(0, 10) round-trips the exact typed date identically for every
// viewer, regardless of timezone. Do not "fix" this to match the signup flow.
function dateOnlyToISOString(dateStr) {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toISOString();
}

// One definition of "same email" for this page — trimmed + lowercased. Firestore
// string equality is case-sensitive and CreateAccount.js stores the email as
// typed (see the Case B recon), so every email comparison here — duplicate-trial
// detection AND the account lookup — must go through this, never a
// where('email','==') query, which would silently miss a mixed-case account and
// tell staff "no account exists" for a customer who has one.
function normalizeEmail(v) {
  return (v || '').trim().toLowerCase();
}

function findDuplicates(trials, email, phone, excludeId) {
  const e = normalizeEmail(email);
  const p = (phone || '').trim();
  if (!e && !p) return [];
  return trials.filter(t => {
    if (t.id === excludeId) return false;
    const matchEmail = e && normalizeEmail(t.parentEmail) === e;
    const matchPhone = p && (t.parentPhone || '').trim() === p;
    return matchEmail || matchPhone;
  });
}

// Reason codes linkTrialToAccount returns, rendered for staff. A missing key
// falls back to the raw code so a new reason is still legible.
const LINK_REASON_TEXT = {
  'nothing-to-do': 'missing trial or account reference',
  'no-account': 'that account no longer exists',
  'no-trial': 'the trial record no longer exists',
  'already-linked': 'this trial is already attached to an account',
  'already-converted': 'this trial has a completed payment and is already on an account',
  'email-mismatch': 'the trial email and the account email no longer match',
  'no-student-name': 'the trial has no student name',
  'adult-account-single-learner': 'an adult account already holds its one learner — an adult wanting another course should renew, and a child needs their own family account',
  'account-type-mismatch': 'an adult learner needs their own adult account — this trial and the account disagree',
  'count-mismatch': 'the account changed mid-write — nothing was saved, try again',
  'child-missing': 'the account changed mid-write — nothing was saved, try again',
  'original-child-touched': 'the account changed mid-write — nothing was saved, try again',
  'link-threw': 'the link write failed — try again',
};

// ── Remove courseless trial-child entries from a parent's users doc ──────────
// Called from the delete flow when an admin chooses "delete trial AND remove the
// child". This writes to a PAYING CUSTOMER's account document, so it is
// deliberately paranoid:
//
//   • It removes ONLY entries whose child.id is in `studentIds` — the exact ids
//     the deleted trial docs point at via trial.studentId. Never matched by name
//     or array position.
//   • AND whose child.course == null (== catches null OR a missing key). A child
//     with ANY course object is a real enrolment and is NEVER removed, whatever
//     the caller asks for.
//   • The write is ABORTED (nothing written) unless the resulting array is
//     exactly the original minus the courseless targets — no more, no less. A
//     duplicate-id array, a concurrent edit, or any surprise → no write.
//   • Runs in a transaction, so course:null is checked against the document at
//     commit time, not a stale read (a payment webhook could be attaching a
//     course to that very child as the admin clicks).
//
// Returns { removed, keptWithCourse, reason }. removed is the count actually
// dropped; keptWithCourse counts targets skipped because they had a course.
async function removeCourselessTrialChildren(userId, studentIds) {
  const idSet = new Set((studentIds || []).filter(Boolean));
  if (!userId || idSet.size === 0) {
    return { removed: 0, keptWithCourse: 0, reason: 'nothing-to-do' };
  }

  const userRef = doc(db, 'users', userId);
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(userRef);
    if (!snap.exists()) return { removed: 0, keptWithCourse: 0, reason: 'no-user-doc' };

    const data = snap.data();
    const children = Array.isArray(data.children) ? data.children : [];

    const targets = children.filter(c => c && idSet.has(c.id));
    const removableIds = new Set(
      targets.filter(c => c.course == null).map(c => c.id)
    );
    const keptWithCourse = targets.length - removableIds.size;

    if (removableIds.size === 0) {
      return { removed: 0, keptWithCourse, reason: 'no-courseless-target' };
    }

    const next = children.filter(c => !(c && removableIds.has(c.id)));

    // Hard invariant: dropped exactly removableIds.size entries, nothing else.
    if (next.length !== children.length - removableIds.size) {
      return { removed: 0, keptWithCourse, reason: 'count-mismatch' };
    }
    // Every intended id is gone; every other child is still present.
    const nextIds = new Set(next.map(c => c && c.id));
    for (const id of removableIds) {
      if (nextIds.has(id)) return { removed: 0, keptWithCourse, reason: 'target-survived' };
    }

    tx.update(userRef, { children: next, updatedAt: new Date().toISOString() });
    return { removed: removableIds.size, keptWithCourse, reason: null };
  });
}

// ── Link an admin-created trial to an existing customer account (Case B) ──────
// The mirror image of removeCourselessTrialChildren: it APPENDS exactly one
// courseless child to a PAYING CUSTOMER's users doc and points the trial at it.
// Same paranoia, opposite direction — a reason code on every refusal, never a
// partial write, everything inside one transaction so every check holds against
// the document at commit time, not a stale read.
//
// Why this exists: a trial staff logged in this page starts with userId/
// studentId null. Until it's linked, the parent can't see it on /trial-status
// and can't pay for it. Linking is ALWAYS an explicit staff action behind a
// confirmation — never automatic on an email match — because an auto-link on a
// mistyped address would write a child into a stranger's paid account.
//
// Writes, both in the same transaction:
//   users/{targetUser.id}.children += { id, name, dateOfBirth, createdAt,
//                                       order, course: null }
//   trials/{trial.id}             += { userId, studentId, linkedAt, linkedBy,
//                                       updatedAt }
// It does NOT touch status / statusHistory / scheduledAt / teacherId /
// teacherName / meetingUrl — the link is not a status change, and statusHistory
// entries must each be a real transition. It does NOT write signupSource:'trial'
// onto the user doc: this is an existing customer, and that stamp is false data
// ProtectedRoute and Login both key off.
//
// course MUST be null — not undefined, not omitted, never { status:'pending' }.
// A courseless child is what keeps this student out of the ProtectedRoute
// access ladder, out of CourseRenewal, and out of cleanupExpiredAccount;
// { status:'pending' } on a child would make the cleanup job's .every() test
// read the whole account as abandoned.
//
// This write fires onUserUpdate. Per the Case B recon (A4): that trigger does
// one read of the classes collection and writes nothing — a brand-new courseless
// child id cannot appear in any class's enrolledStudents. Same already-accepted
// behaviour as createTrialSignup and the webhook's additional_children branch.
// Not a bug — do not try to suppress it.
//
// Returns { linked: boolean, reason: string|null, childId: string|null }.
async function linkTrialToAccount(trial, targetUser, by) {
  if (!trial?.id || !targetUser?.id) {
    return { linked: false, reason: 'nothing-to-do', childId: null };
  }

  const userRef = doc(db, 'users', targetUser.id);
  const trialRef = doc(db, 'trials', trial.id);

  return runTransaction(db, async (tx) => {
    const userSnap = await tx.get(userRef);
    const trialSnap = await tx.get(trialRef);
    if (!userSnap.exists()) return { linked: false, reason: 'no-account', childId: null };
    if (!trialSnap.exists()) return { linked: false, reason: 'no-trial', childId: null };

    const userData = userSnap.data();
    const t = trialSnap.data();

    // Every refusal re-checked against the fresh docs — another admin, or a
    // payment webhook, may have moved either one between the click and here.
    if (t.userId) return { linked: false, reason: 'already-linked', childId: null };
    if (t.studentId) return { linked: false, reason: 'already-linked', childId: null };
    // A real payment. convertedPackageId is webhook-only and sacred — never
    // relink a trial that money already moved through.
    if (t.convertedPackageId) return { linked: false, reason: 'already-converted', childId: null };

    const studentName = (t.studentName || '').trim();
    if (!studentName) return { linked: false, reason: 'no-student-name', childId: null };

    if (normalizeEmail(t.parentEmail) !== normalizeEmail(userData.email)) {
      return { linked: false, reason: 'email-mismatch', childId: null };
    }

    // An adult account holds exactly ONE learner — the account holder. Linking
    // appends a NEW courseless child, so any link to an adult account creates a
    // second learner entry (and, on conversion, a second course) for what is
    // meant to be a single-person account. Refuse EVERY link to an adult
    // account, whatever the trial's studentType:
    //   - a lapsed adult wanting more classes uses /course-renewal, which
    //     already handles completed and expired courses — they never need a
    //     second learner entry;
    //   - a child on that email needs their own family account.
    // `=== 'adult'` is the only positive test; a legacy account with no
    // accountType is a family account. This is a staff action with an on-screen
    // explanation, not the customer checkout path, so it refuses rather than
    // failing open.
    if (userData.accountType === 'adult') {
      return { linked: false, reason: 'adult-account-single-learner', childId: null };
    }
    // Family account + adult trial: an adult learner needs their own adult
    // account, not a child entry on a family one.
    if (t.studentType === 'adult') {
      return { linked: false, reason: 'account-type-mismatch', childId: null };
    }

    const children = Array.isArray(userData.children) ? userData.children : [];
    const existingIds = new Set(children.map(c => c && c.id).filter(Boolean));

    // child_<ms>_<n>, n climbing from 0 until the id is free. Every other
    // generation site (CreateAccount, createTrialSignup, the webhook) trusts the
    // timestamp alone; here two links for one family in the same millisecond, or
    // a re-run of a repair, could collide — so this site checks (recon A3).
    const ts = Date.now();
    let n = 0;
    let childId = `child_${ts}_${n}`;
    while (existingIds.has(childId)) {
      n += 1;
      childId = `child_${ts}_${n}`;
    }

    const nowISO = new Date().toISOString();
    const newChild = {
      id: childId,
      name: studentName,
      dateOfBirth: t.dateOfBirth ?? null,
      createdAt: nowISO,
      order: children.length,
      course: null,
    };
    const next = [...children, newChild];

    // Hard invariants — abort (write nothing) on any surprise:
    if (next.length !== children.length + 1) {
      return { linked: false, reason: 'count-mismatch', childId: null };
    }
    if (!next.some(c => c && c.id === childId)) {
      return { linked: false, reason: 'child-missing', childId: null };
    }
    // Every original entry must be the SAME OBJECT — nothing mutated or dropped.
    for (let i = 0; i < children.length; i += 1) {
      if (next[i] !== children[i]) {
        return { linked: false, reason: 'original-child-touched', childId: null };
      }
    }

    tx.update(userRef, { children: next, updatedAt: nowISO });
    tx.update(trialRef, {
      userId: targetUser.id,
      studentId: childId,
      linkedAt: nowISO,
      linkedBy: by ?? null,
      updatedAt: nowISO,
    });

    return { linked: true, reason: null, childId };
  });
}

function validateTrialForm(form, isEditing) {
  const e = {};
  if (!form.parentName?.trim()) {
    e.parentName = form.isAdult ? 'Name is required' : "Parent's name is required";
  }
  if (!form.parentEmail?.trim()) {
    e.parentEmail = 'Email is required';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.parentEmail.trim())) {
    e.parentEmail = 'Enter a valid email address';
  }

  const singleRow = isEditing || form.isAdult;

  // Adult mode reuses parentName as the student's name — no per-row name to check.
  if (!form.isAdult) {
    if (singleRow) {
      // Editing an existing child trial — exactly one row, required as before.
      if (!form.students[0]?.studentName?.trim()) {
        e.students = { [form.students[0].key]: "Student's name is required" };
      }
    } else {
      // Multi-student create — blank rows are silently dropped at submit
      // (handleSubmit filters them), not individually flagged. Only block
      // when every row is blank, so there's nothing left to create.
      const anyFilled = form.students.some(s => s.studentName?.trim());
      if (!anyFilled) {
        e.studentsGeneral = 'At least one student is required.';
      }
    }
  }

  // Schedule is per student row now. A non-first row that's still "same as
  // first" inherits row 0's values and has nothing of its own to validate.
  form.students.forEach((s, i) => {
    const inheritsSchedule = i > 0 && !singleRow && s.sameAsFirst;
    if (inheritsSchedule) return;
    const hasDate = !!s.dateStr;
    const hasTime = !!s.timeStr;
    if (hasDate !== hasTime) {
      e.schedule = e.schedule || {};
      e.schedule[s.key] = 'Provide both a date and a time, or leave both blank';
    }
    if ((hasDate || hasTime) && !s.teacherId) {
      e.teacherId = e.teacherId || {};
      e.teacherId[s.key] = 'Select a teacher to schedule this trial';
    }
  });

  return e;
}

// ─── Status badge ───────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${TRIAL_STATUS_STYLES[status] ?? 'bg-gray-100 text-gray-500'}`}>
      {TRIAL_STATUS_LABELS[status] ?? status}
    </span>
  );
}

// ─── Student Schedule Fields ────────────────────────────────────────────────
// Teacher/date/time/duration/Zoom for one student row. Shared by row 0
// (always shown), any subsequent row with its "same as first" box unticked,
// and the single-row edit/adult layout — one definition, three call sites.

function StudentScheduleFields({ student, onChange, errors, teachers, derivedTz }) {
  return (
    <>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Teacher</label>
        <select
          value={student.teacherId}
          onChange={e => onChange('teacherId', e.target.value)}
          className={`w-full border rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.teacherId ? 'border-red-400' : 'border-gray-300'}`}
        >
          <option value="">Not assigned yet</option>
          {teachers.filter(t => t.active).map(t => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
        {errors.teacherId && <p className="text-xs text-red-500 mt-1">{errors.teacherId}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Scheduled time <span className="text-xs font-normal text-gray-400">(optional — leave blank if not yet arranged)</span>
        </label>
        <div className="grid grid-cols-2 gap-2">
          <input
            type="date"
            value={student.dateStr}
            onChange={e => onChange('dateStr', e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
          <input
            type="time"
            value={student.timeStr}
            onChange={e => onChange('timeStr', e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>
        {derivedTz ? (
          <p className="text-xs text-gray-400 mt-1">
            Times are in <strong className="text-gray-600">{derivedTz}</strong>.
          </p>
        ) : (
          <p className="text-xs text-amber-600 mt-1">
            We can't determine a timezone for this location — enter the time in UTC, and record the parent's local time in Notes.
          </p>
        )}
        {errors.schedule && <p className="text-xs text-red-500 mt-1">{errors.schedule}</p>}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Duration (min)</label>
          <input
            type="number"
            min={15}
            max={120}
            value={student.durationMins}
            onChange={e => onChange('durationMins', parseInt(e.target.value, 10) || DEFAULT_TRIAL_DURATION_MINS)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Zoom link</label>
          <input
            type="text"
            value={student.meetingUrl}
            onChange={e => onChange('meetingUrl', e.target.value)}
            placeholder="https://zoom.us/j/…"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>
      </div>
    </>
  );
}

// ─── Trial Form Modal (create / edit — fields only, never changes status) ──────
// Create supports logging several students from one parent in one submission
// (siblings trialled together), each with their own schedule — the first
// student's teacher/time/duration/Zoom can be copied down to the rest via a
// checkbox, ticked by default, but every row is still its own independent
// trial once saved. Edit always operates on exactly one existing record — no
// add/remove rows, no adult toggle, since studentType is fixed at creation.

function TrialFormModal({ trial, teachers, referralCodes, trials, onSave, onClose, saving, saveResult }) {
  const isEditing = !!trial?.id;

  const initialForm = trial
    ? {
        isAdult: trial.studentType === 'adult',
        parentName: trial.parentName ?? '',
        parentEmail: trial.parentEmail ?? '',
        parentPhone: trial.parentPhone ?? '',
        attribution: {
          code: trial.attribution?.code ?? '',
          heardAbout: trial.attribution?.heardAbout ?? '',
          heardAboutOther: trial.attribution?.heardAboutOther ?? '',
        },
        intake: {
          city: trial.intake?.city ?? '',
          stateProvince: trial.intake?.stateProvince ?? '',
          country: trial.intake?.country ?? '',
          preferredTimes: trial.intake?.preferredTimes ?? '',
          notes: trial.intake?.notes ?? '',
        },
        students: [{
          key: 'edit',
          studentName: trial.studentName ?? '',
          // Stored at UTC midnight (see dateOnlyToISOString) specifically so
          // this date-portion slice round-trips exactly, the same for every
          // viewer regardless of their own timezone.
          dateOfBirth: trial.dateOfBirth ? trial.dateOfBirth.slice(0, 10) : '',
          arabicLevel: trial.intake?.arabicLevel ?? '',
          dialect: trial.intake?.dialect ?? '',
          sameAsFirst: false,
          teacherId: trial.teacherId ?? '',
          // Re-derive the zone from intake on every reopen — nothing is stored on
          // the trial doc itself. Falls back to UTC (never the viewer's browser
          // zone) when the location can't resolve one.
          dateStr: trial.scheduledAt ? isoToDisplayDate(trial.scheduledAt, getTrialTimezone(trial) ?? 'UTC') : '',
          timeStr: trial.scheduledAt ? isoToDisplayTime(trial.scheduledAt, getTrialTimezone(trial) ?? 'UTC') : '',
          durationMins: trial.durationMins ?? DEFAULT_TRIAL_DURATION_MINS,
          meetingUrl: trial.meetingUrl ?? '',
        }],
      }
    : { ...EMPTY_TRIAL_FORM, students: [makeEmptyStudentRow()] };

  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});

  // A partial multi-student failure keeps this modal open with only the
  // failed students left to retry — never re-creates ones that already saved.
  useEffect(() => {
    if (saveResult?.failed?.length > 0) {
      setForm(f => ({
        ...f,
        students: f.students.filter(s => saveResult.failed.some(fl => fl.key === s.key)),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveResult]);

  function setField(field, value) {
    setForm(f => ({ ...f, [field]: value }));
    setErrors(e => { const n = { ...e }; delete n[field]; return n; });
  }

  function setAttribution(field, value) {
    setForm(f => ({ ...f, attribution: { ...f.attribution, [field]: value } }));
  }

  function setIntake(field, value) {
    setForm(f => ({ ...f, intake: { ...f.intake, [field]: value } }));
  }

  function handleContactFieldsChange(partial) {
    setForm(f => {
      const next = { ...f };
      if ('phoneNumber' in partial) next.parentPhone = partial.phoneNumber;
      const intakePatch = {};
      if ('country' in partial) intakePatch.country = partial.country;
      if ('stateProvince' in partial) intakePatch.stateProvince = partial.stateProvince;
      if ('city' in partial) intakePatch.city = partial.city;
      if (Object.keys(intakePatch).length > 0) next.intake = { ...f.intake, ...intakePatch };
      return next;
    });
  }

  function setStudentField(key, field, value) {
    setForm(f => ({
      ...f,
      students: f.students.map(s => (s.key === key ? { ...s, [field]: value } : s)),
    }));
    setErrors(e => {
      const n = { ...e };
      if (n.students?.[key]) {
        const ns = { ...n.students }; delete ns[key];
        if (Object.keys(ns).length === 0) delete n.students; else n.students = ns;
      }
      if (n.schedule?.[key]) {
        const nsc = { ...n.schedule }; delete nsc[key];
        if (Object.keys(nsc).length === 0) delete n.schedule; else n.schedule = nsc;
      }
      if (n.teacherId?.[key]) {
        const nt = { ...n.teacherId }; delete nt[key];
        if (Object.keys(nt).length === 0) delete n.teacherId; else n.teacherId = nt;
      }
      return n;
    });
  }

  function addStudentRow() {
    setForm(f => ({ ...f, students: [...f.students, makeEmptyStudentRow(f.students[0])] }));
  }

  function removeStudentRow(key) {
    setForm(f => ({ ...f, students: f.students.filter(s => s.key !== key) }));
  }

  // Ticking just flips the flag — the effective values are read live from row
  // 0 at render/submit time. Unticking snapshots row 0's CURRENT values into
  // this row as an editable starting point (not whatever was there before).
  function toggleSameAsFirst(key, checked) {
    setForm(f => {
      const first = f.students[0];
      return {
        ...f,
        students: f.students.map(s => {
          if (s.key !== key) return s;
          if (checked) return { ...s, sameAsFirst: true };
          return {
            ...s,
            sameAsFirst: false,
            teacherId: first.teacherId,
            dateStr: first.dateStr,
            timeStr: first.timeStr,
            durationMins: first.durationMins,
            meetingUrl: first.meetingUrl,
          };
        }),
      };
    });
  }

  function handleAdultToggle(checked) {
    setForm(f => ({
      ...f,
      isAdult: checked,
      // Collapse to a single row when switching to adult — a self-learner is
      // one student. Keeps whatever fields were already entered on row 0.
      students: checked ? [f.students[0] ?? makeEmptyStudentRow()] : f.students,
    }));
    setErrors({});
  }

  const duplicates = useMemo(
    () => findDuplicates(trials, form.parentEmail, form.parentPhone, trial?.id),
    [trials, form.parentEmail, form.parentPhone, trial?.id]
  );

  // ── Case B (C1): does an account already exist for this email? ──────────────
  // Create only — an existing trial is linked from its row (C2), not here. The
  // whole users collection is read ONCE when the create modal opens (never in
  // the page's fetchAll — most sessions never open this modal), then every
  // keystroke on the email field re-matches client-side, case-insensitively,
  // against that snapshot. A where('email','==') query is deliberately NOT used:
  // it would miss mixed-case CreateAccount-era accounts (recon A1/A2).
  //
  // THREE distinct states, rendered differently — the absence of data must
  // never render as evidence of absence:
  //   loading → a quiet "checking…" line, NOT the no-match note. Otherwise, in
  //             the window between opening the modal and the read landing, a
  //             known customer's email reads as "no account — send them a link",
  //             which is the single worst wrong answer this feature can give.
  //   ready   → match panel, or the grey no-match note.
  //   error   → nothing. No claim either way.
  const [accountLookup, setAccountLookup] = useState({
    status: isEditing ? 'ready' : 'loading',
    users: [],
  });
  const [linkToAccount, setLinkToAccount] = useState(false);
  const [linkClashAck, setLinkClashAck] = useState(false);

  useEffect(() => {
    if (isEditing) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'users'));
        if (!cancelled) {
          setAccountLookup({ status: 'ready', users: snap.docs.map(d => ({ id: d.id, ...d.data() })) });
        }
      } catch (err) {
        console.error('TrialFormModal account lookup failed:', err);
        if (!cancelled) setAccountLookup({ status: 'error', users: [] });
      }
    })();
    return () => { cancelled = true; };
  }, [isEditing]);

  const emailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.parentEmail.trim());
  const showAccountLookup = !isEditing && emailLooksValid;

  const matchedAccount = useMemo(() => {
    if (!showAccountLookup || accountLookup.status !== 'ready') return null;
    const e = normalizeEmail(form.parentEmail);
    return accountLookup.users.find(u => normalizeEmail(u.email) === e) || null;
  }, [showAccountLookup, accountLookup, form.parentEmail]);

  const matchedEnrolledCount = useMemo(
    () => (matchedAccount?.children || []).filter(c => c && c.course).length,
    [matchedAccount]
  );

  // FIX 2 — same name-clash guard C2 has. If the matched account already has a
  // child whose name matches one being entered here, ticking the link box would
  // put a second same-named child on a paying account (one coursed, one not) —
  // confusing on /enroll, in AccountSettings and the admin Children column. Not
  // blocked (a real sibling can share a name), but ticking then requires the
  // same explicit "different student" acknowledgement.
  const clashingNames = useMemo(() => {
    if (!matchedAccount) return [];
    const existing = new Set(
      (matchedAccount.children || [])
        .map(c => c && (c.name || '').trim().toLowerCase())
        .filter(Boolean)
    );
    const entered = form.isAdult ? [form.parentName] : form.students.map(s => s.studentName);
    return [...new Set(
      entered
        .map(n => (n || '').trim())
        .filter(n => n && existing.has(n.toLowerCase()))
    )];
  }, [matchedAccount, form.isAdult, form.parentName, form.students]);

  // Item 4 — when linking is not allowed. Two reasons, different messages,
  // both suppress the checkbox and both refused by linkTrialToAccount:
  //   'adult-account'  — the matched account is an adult self-learner account.
  //     It holds exactly one learner; linking would append a second. Applies
  //     whatever the trial's type — a lapsed adult renews at /course-renewal,
  //     a child needs their own family account.
  //   'type-mismatch'  — family account + adult trial. An adult learner needs
  //     their own adult account.
  // `=== 'adult'` on each side; a legacy no-accountType account is a family
  // account. `form.isAdult` is the whole submission's type (adult mode = one
  // student).
  const linkBlockedReason = !matchedAccount
    ? null
    : matchedAccount.accountType === 'adult'
      ? 'adult-account'
      : form.isAdult
        ? 'type-mismatch'
        : null;
  const linkBlocked = linkBlockedReason !== null;

  // A stale tick / ack must never survive the email changing — whether to one
  // with no account or to a DIFFERENT account. Re-confirm whenever the matched
  // account id moves (including to none).
  const matchedAccountId = matchedAccount?.id ?? null;
  useEffect(() => {
    setLinkToAccount(false);
    setLinkClashAck(false);
  }, [matchedAccountId]);

  const linkClashUnresolved = linkToAccount && clashingNames.length > 0 && !linkClashAck;
  const linkEffective = linkToAccount && !!matchedAccount && !linkBlocked && !linkClashUnresolved;

  // Derived live from the current country/state fields — never a manual dropdown,
  // so the admin can't pick a zone that disagrees with the location on file.
  // Same zone applies to every student row in this submission (location is
  // parent-level); only the date/time per row differs.
  const derivedTz = resolveTimezone(form.intake.country, form.intake.stateProvince);

  function handleSubmit(e) {
    e.preventDefault();
    const v = validateTrialForm(form, isEditing);
    if (Object.keys(v).length > 0) { setErrors(v); return; }
    // Blank rows are dropped silently here (not per-row errors) — validation
    // above only checked that at least one row survives this filter.
    const cleanedStudents = (isEditing || form.isAdult)
      ? form.students
      : form.students.filter(s => s.studentName?.trim());
    onSave({
      ...form,
      students: cleanedStudents,
      id: trial?.id,
      // Only a live, ticked match with any name-clash acknowledged — never a
      // stale id. Create only. (Submit is disabled while a clash is unresolved,
      // so this is belt-and-braces.)
      linkTargetUserId: (!isEditing && linkEffective) ? matchedAccount.id : null,
    });
  }

  const activeCodes = (referralCodes ?? []).filter(c => c.active);
  // Single-row layout whenever editing (any studentType) or creating as an
  // adult self-learner. The repeatable multi-student rows only appear when
  // creating for a child/children.
  const singleRowLayout = isEditing || form.isAdult;
  const singleStudent = form.students[0];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">
            {isEditing ? 'Edit Trial' : 'Log a Trial'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {saveResult?.failed?.length > 0 && (
            <div className="bg-red-50 border border-red-300 rounded-lg px-3 py-2.5 space-y-1">
              <p className="text-xs font-semibold text-red-800 flex items-center gap-1.5">
                <AlertTriangle size={13} /> Some students couldn't be saved
              </p>
              {saveResult.succeeded.length > 0 && (
                <p className="text-xs text-green-700">
                  Already saved — not re-created: {saveResult.succeeded.map(s => s.studentName).join(', ')}
                </p>
              )}
              <ul className="space-y-0.5">
                {saveResult.failed.map(f => (
                  <li key={f.key} className="text-xs text-red-700">{f.studentName}: {f.message}</li>
                ))}
              </ul>
              <p className="text-xs text-red-600">Only the student(s) below remain in this form — retry to save them.</p>
            </div>
          )}

          {/* Adult toggle — create only; studentType is fixed once a trial exists */}
          {!isEditing && (
            <label className="flex items-center gap-2.5 cursor-pointer bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2.5">
              <input
                type="checkbox"
                checked={form.isAdult}
                onChange={e => handleAdultToggle(e.target.checked)}
                className="w-4 h-4 rounded border-indigo-400 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm font-medium text-indigo-800">
                Student is an adult (self-learner) — not a child account
              </span>
            </label>
          )}

          {/* Parent / self-learner name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {form.isAdult ? 'Name' : "Parent's name"} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.parentName}
              onChange={e => setField('parentName', e.target.value)}
              className={`w-full border rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.parentName ? 'border-red-400' : 'border-gray-300'}`}
            />
            {errors.parentName && <p className="text-xs text-red-500 mt-1">{errors.parentName}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={form.parentEmail}
              onChange={e => setField('parentEmail', e.target.value)}
              className={`w-full border rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.parentEmail ? 'border-red-400' : 'border-gray-300'}`}
            />
            {errors.parentEmail && <p className="text-xs text-red-500 mt-1">{errors.parentEmail}</p>}
          </div>

          {duplicates.length > 0 && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg px-3 py-2.5 space-y-1">
              <p className="text-xs font-semibold text-amber-800 flex items-center gap-1.5">
                <Info size={13} /> This contact has trialled before
              </p>
              <ul className="space-y-0.5">
                {duplicates.map(d => (
                  <li key={d.id} className="text-xs text-amber-700">
                    {d.studentName} — {formatDateShort(d.createdAt)} — {TRIAL_STATUS_LABELS[d.status] ?? d.status}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Case B (C1) — account lookup. Separate signal from the
              duplicate-trial warning above (that checks `trials`, this checks
              `users`); both can show at once. Create only. Three states —
              loading / matched / no-match — plus silent on a failed read. */}
          {showAccountLookup && accountLookup.status === 'loading' && (
            <p className="text-xs text-gray-400 flex items-center gap-1.5">
              <RefreshCw size={12} className="animate-spin" /> Checking for an existing account…
            </p>
          )}

          {showAccountLookup && accountLookup.status === 'ready' && matchedAccount && (
            <div className="bg-purple-50 border border-purple-300 rounded-lg px-3 py-2.5 space-y-2">
              <p className="text-xs font-semibold text-purple-800 flex items-center gap-1.5">
                <LinkIcon size={13} /> This email belongs to an existing account
              </p>
              <p className="text-xs text-purple-700">
                {[matchedAccount.firstName, matchedAccount.lastName].filter(Boolean).join(' ') || matchedAccount.email}
                {' · '}
                {matchedAccount.accountType === 'adult' ? 'adult account' : 'family account'}
                {' · '}
                {matchedEnrolledCount} enrolled {matchedEnrolledCount === 1 ? 'child' : 'children'}
              </p>

              {linkBlocked ? (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2.5">
                  <AlertTriangle size={13} className="text-amber-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-800">
                    {linkBlockedReason === 'adult-account'
                      ? "This is an adult self-learner account — it already holds its one learner, the account holder. An adult wanting another course renews at /course-renewal; a child needs their own family account. This trial can't be linked here."
                      : 'This is a family account and this trial is for an adult learner. An adult learner needs their own adult account — this trial cannot be linked here.'}
                  </p>
                </div>
              ) : (
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={linkToAccount}
                    onChange={e => setLinkToAccount(e.target.checked)}
                    className="w-4 h-4 mt-0.5 rounded border-purple-400 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="text-xs text-purple-800">
                    Link this trial to their account so they can see it and enrol.
                    {!isEditing && !form.isAdult && form.students.length > 1
                      && ' Every student in this submission links to the same account.'}
                  </span>
                </label>
              )}

              {linkToAccount && !linkBlocked && clashingNames.length > 0 && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-300 rounded-lg px-3 py-2.5">
                  <AlertTriangle size={13} className="text-red-500 mt-0.5 flex-shrink-0" />
                  <div className="space-y-1.5">
                    <p className="text-xs text-red-700">
                      This account already has a child named{' '}
                      <strong>{clashingNames.map(n => `"${n}"`).join(', ')}</strong>. That is usually
                      the same student, already on the account by another route — linking would create
                      a duplicate child on a paying customer.
                    </p>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={linkClashAck}
                        onChange={e => setLinkClashAck(e.target.checked)}
                        className="rounded border-red-400 text-red-600"
                      />
                      <span className="text-xs font-medium text-red-800">
                        I've checked — {clashingNames.length === 1 ? 'this is a different student' : 'these are different students'}
                      </span>
                    </label>
                  </div>
                </div>
              )}
            </div>
          )}

          {showAccountLookup && accountLookup.status === 'ready' && !matchedAccount && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
              <p className="text-xs text-gray-600 flex items-start gap-1.5">
                <Info size={13} className="text-gray-400 mt-0.5 flex-shrink-0" />
                <span>
                  No account for this email. Send them your trial link instead so they sign up
                  themselves — a trial created without an account can't be seen or paid for by the
                  parent until it's linked.
                </span>
              </p>
            </div>
          )}

          {/* Contact fields — shared component, unmodified */}
          <ProfileContactFields
            values={{
              phoneNumber: form.parentPhone,
              country: form.intake.country,
              stateProvince: form.intake.stateProvince,
              city: form.intake.city,
            }}
            onChange={handleContactFieldsChange}
          />

          {/* Student(s) */}
          {singleRowLayout ? (
            <div className="space-y-3 border-t border-gray-100 pt-4">
              {!form.isAdult && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Student's name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={singleStudent.studentName}
                    onChange={e => setStudentField(singleStudent.key, 'studentName', e.target.value)}
                    className={`w-full border rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.students?.[singleStudent.key] ? 'border-red-400' : 'border-gray-300'}`}
                  />
                  {errors.students?.[singleStudent.key] && <p className="text-xs text-red-500 mt-1">{errors.students[singleStudent.key]}</p>}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date of birth {!form.isAdult && <span className="text-xs font-normal text-gray-400">(optional)</span>}
                  </label>
                  <input
                    type="date"
                    value={singleStudent.dateOfBirth}
                    onChange={e => setStudentField(singleStudent.key, 'dateOfBirth', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Arabic level</label>
                  <input
                    type="text"
                    value={singleStudent.arabicLevel}
                    onChange={e => setStudentField(singleStudent.key, 'arabicLevel', e.target.value)}
                    placeholder="e.g. Beginner"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Dialect</label>
                <select
                  value={singleStudent.dialect}
                  onChange={e => setStudentField(singleStudent.key, 'dialect', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Not sure</option>
                  {TRIAL_DIALECTS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
              </div>

              <div className="space-y-3 border-t border-gray-100 pt-3">
                <StudentScheduleFields
                  student={singleStudent}
                  onChange={(field, value) => setStudentField(singleStudent.key, field, value)}
                  errors={{
                    teacherId: errors.teacherId?.[singleStudent.key],
                    schedule: errors.schedule?.[singleStudent.key],
                  }}
                  teachers={teachers}
                  derivedTz={derivedTz}
                />
                <p className="text-xs text-gray-400">
                  {isEditing
                    ? "Editing the schedule here changes the details only — it does not change the trial's status."
                    : 'Filling this in schedules the trial immediately.'}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-3 border-t border-gray-100 pt-4">
              <label className="block text-sm font-medium text-gray-700">
                Student{form.students.length > 1 ? 's' : ''} <span className="text-red-500">*</span>
              </label>
              {errors.studentsGeneral && <p className="text-xs text-red-500">{errors.studentsGeneral}</p>}
              {form.students.map((s, i) => (
                <div key={s.key} className="border border-gray-200 rounded-lg p-3 space-y-2 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Student {i + 1}</p>
                    {form.students.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeStudentRow(s.key)}
                        className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Remove this student"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                  <div>
                    <input
                      type="text"
                      value={s.studentName}
                      onChange={e => setStudentField(s.key, 'studentName', e.target.value)}
                      placeholder="Student's name"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="date"
                      value={s.dateOfBirth}
                      onChange={e => setStudentField(s.key, 'dateOfBirth', e.target.value)}
                      title="Date of birth (optional)"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <input
                      type="text"
                      value={s.arabicLevel}
                      onChange={e => setStudentField(s.key, 'arabicLevel', e.target.value)}
                      placeholder="Arabic level"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <select
                    value={s.dialect}
                    onChange={e => setStudentField(s.key, 'dialect', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Dialect — not sure</option>
                    {TRIAL_DIALECTS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>

                  {i === 0 ? (
                    <div className="space-y-2 border-t border-gray-200 pt-2">
                      <StudentScheduleFields
                        student={s}
                        onChange={(field, value) => setStudentField(s.key, field, value)}
                        errors={{ teacherId: errors.teacherId?.[s.key], schedule: errors.schedule?.[s.key] }}
                        teachers={teachers}
                        derivedTz={derivedTz}
                      />
                    </div>
                  ) : (
                    <div className="border-t border-gray-200 pt-2 space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={s.sameAsFirst}
                          onChange={e => toggleSameAsFirst(s.key, e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="text-xs text-gray-600">
                          Same time and teacher as {form.students[0].studentName?.trim() || 'the first student'}
                        </span>
                      </label>
                      {!s.sameAsFirst && (
                        <StudentScheduleFields
                          student={s}
                          onChange={(field, value) => setStudentField(s.key, field, value)}
                          errors={{ teacherId: errors.teacherId?.[s.key], schedule: errors.schedule?.[s.key] }}
                          teachers={teachers}
                          derivedTz={derivedTz}
                        />
                      )}
                    </div>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addStudentRow}
                className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 font-medium"
              >
                <Plus size={13} /> Add another student
              </button>
              <p className="text-xs text-gray-400">
                Each student becomes its own trial record, sharing this parent's details — they can be
                rescheduled or resolved individually afterwards. A student row with a teacher and time filled
                in is created as Scheduled; a blank one is created as Requested.
              </p>
            </div>
          )}

          {/* Attribution */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Referral code</label>
              <select
                value={form.attribution.code}
                onChange={e => setAttribution('code', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="">None</option>
                {activeCodes.map(c => (
                  <option key={c.id} value={c.id}>{c.label} ({c.type})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">How did they hear about us?</label>
              <select
                value={form.attribution.heardAbout}
                onChange={e => setAttribution('heardAbout', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Not sure</option>
                {HEARD_ABOUT_OPTIONS.map(o => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
          </div>
          {form.attribution.heardAbout === 'Other' && (
            <input
              type="text"
              value={form.attribution.heardAboutOther}
              onChange={e => setAttribution('heardAboutOther', e.target.value)}
              placeholder="Please specify"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          )}

          {/* Intake extras — shared across all students in this submission */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Preferred times</label>
            <input
              type="text"
              value={form.intake.preferredTimes}
              onChange={e => setIntake('preferredTimes', e.target.value)}
              placeholder="e.g. Weekday evenings"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={form.intake.notes}
              onChange={e => setIntake('notes', e.target.value)}
              rows={2}
              placeholder="Anything staff should know — including the stated timezone if it's outside our usual coverage"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={saving || linkClashUnresolved}
              className="flex-1 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              {saving && <RefreshCw size={14} className="animate-spin" />}
              {isEditing
                ? 'Save Changes'
                : !singleRowLayout && form.students.length > 1
                  ? `Log ${form.students.length} Trials`
                  : 'Log Trial'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Schedule Modal (requested → scheduled) ────────────────────────────────────

function ScheduleModal({ trial, teachers, onConfirm, onClose, saving }) {
  // Static for this modal's lifetime — ScheduleModal doesn't edit intake, so the
  // zone can't change mid-edit the way it can in TrialFormModal.
  const derivedTz = getTrialTimezone(trial);
  const effectiveTz = derivedTz ?? 'UTC';
  const [teacherId, setTeacherId] = useState(trial.teacherId ?? '');
  const [dateStr, setDateStr] = useState('');
  const [timeStr, setTimeStr] = useState('');
  const [durationMins, setDurationMins] = useState(trial.durationMins ?? DEFAULT_TRIAL_DURATION_MINS);
  const [meetingUrl, setMeetingUrl] = useState(trial.meetingUrl ?? '');
  const [error, setError] = useState(null);

  function handleConfirm() {
    if (!teacherId) { setError('Select a teacher.'); return; }
    if (!dateStr || !timeStr) { setError('Select a date and time.'); return; }
    const scheduledAtISO = localDateTimeToUTCISO(dateStr, timeStr, effectiveTz);
    if (!scheduledAtISO) { setError('Could not resolve that date/time — try again.'); return; }
    onConfirm({ teacherId, scheduledAtISO, durationMins, meetingUrl: meetingUrl.trim() || null });
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">Schedule Trial</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={18} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">{trial.studentName} — {trial.parentName}</p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Teacher <span className="text-red-500">*</span></label>
            <select
              value={teacherId}
              onChange={e => setTeacherId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Select teacher…</option>
              {teachers.filter(t => t.active).map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date & time <span className="text-red-500">*</span></label>
            <div className="grid grid-cols-2 gap-3">
              <input type="date" value={dateStr} onChange={e => setDateStr(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
              <input type="time" value={timeStr} onChange={e => setTimeStr(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
            </div>
            {derivedTz ? (
              <p className="text-xs text-gray-400 mt-1">
                Times are in <strong className="text-gray-600">{derivedTz}</strong> (from {[trial.intake?.stateProvince, trial.intake?.country].filter(Boolean).join(', ') || 'the trial\'s location'}).
              </p>
            ) : (
              <p className="text-xs text-amber-600 mt-1">
                We can't determine a timezone for this location — enter the time in UTC, and record the parent's local time in Notes.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Duration (min)</label>
              <input type="number" min={15} max={120} value={durationMins}
                onChange={e => setDurationMins(parseInt(e.target.value, 10) || DEFAULT_TRIAL_DURATION_MINS)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Zoom link</label>
              <input type="text" value={meetingUrl} onChange={e => setMeetingUrl(e.target.value)}
                placeholder="https://zoom.us/j/…"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
            </div>
          </div>

          {error && <p className="text-xs text-red-500">{error}</p>}

          <div className="flex gap-3">
            <button onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button onClick={handleConfirm} disabled={saving}
              className="flex-1 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {saving && <RefreshCw size={14} className="animate-spin" />}
              Schedule
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Reschedule Modal (scheduled/no_show/cancelled → scheduled) ───────────────
// Structure and interaction match AttendanceManager.js's RescheduleModal
// (RenewalManager.js has no RescheduleModal — that file only has a Pause flow):
// header + close, a consequence sentence, the fields, an error line, then
// Cancel / [action] buttons with the same yellow "reschedule" button color.
// Two deliberate departures from that pattern: no manual timezone dropdown
// (trials keep the derived-only zone behaviour throughout this page) and no
// back-date option (a trial is always rescheduled to a future slot, unlike a
// class session that may need back-dating to record what already happened).

function RescheduleModal({ trial, teachers, onConfirm, onClose, saving }) {
  const derivedTz = getTrialTimezone(trial);
  const effectiveTz = derivedTz ?? 'UTC';
  const [teacherId, setTeacherId] = useState(trial.teacherId ?? '');
  const [dateStr, setDateStr] = useState('');
  const [timeStr, setTimeStr] = useState('');
  const [durationMins, setDurationMins] = useState(trial.durationMins ?? DEFAULT_TRIAL_DURATION_MINS);
  const [meetingUrl, setMeetingUrl] = useState(trial.meetingUrl ?? '');
  const [error, setError] = useState(null);

  function handleConfirm() {
    if (!teacherId) { setError('Select a teacher.'); return; }
    if (!dateStr || !timeStr) { setError('Select a date and time.'); return; }
    const scheduledAtISO = localDateTimeToUTCISO(dateStr, timeStr, effectiveTz);
    if (!scheduledAtISO) { setError('Could not resolve that date/time — try again.'); return; }
    onConfirm({ teacherId, scheduledAtISO, durationMins, meetingUrl: meetingUrl.trim() || null });
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">Reschedule Trial</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={18} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            {trial.studentName} — {trial.parentName}. This moves the trial to a new time and sets its
            status back to <strong>Scheduled</strong>.
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Teacher <span className="text-red-500">*</span></label>
            <select
              value={teacherId}
              onChange={e => setTeacherId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Select teacher…</option>
              {teachers.filter(t => t.active).map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New date & time <span className="text-red-500">*</span></label>
            <div className="grid grid-cols-2 gap-3">
              <input type="date" value={dateStr} onChange={e => setDateStr(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
              <input type="time" value={timeStr} onChange={e => setTimeStr(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
            </div>
            {derivedTz ? (
              <p className="text-xs text-gray-400 mt-1">
                Times are in <strong className="text-gray-600">{derivedTz}</strong> (from {[trial.intake?.stateProvince, trial.intake?.country].filter(Boolean).join(', ') || 'the trial\'s location'}).
              </p>
            ) : (
              <p className="text-xs text-amber-600 mt-1">
                We can't determine a timezone for this location — enter the time in UTC, and record the parent's local time in Notes.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Duration (min)</label>
              <input type="number" min={15} max={120} value={durationMins}
                onChange={e => setDurationMins(parseInt(e.target.value, 10) || DEFAULT_TRIAL_DURATION_MINS)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Zoom link</label>
              <input type="text" value={meetingUrl} onChange={e => setMeetingUrl(e.target.value)}
                placeholder="https://zoom.us/j/…"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
            </div>
          </div>

          {error && <p className="text-xs text-red-500">{error}</p>}

          <div className="flex gap-3">
            <button onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button onClick={handleConfirm} disabled={saving}
              className="flex-1 px-4 py-2 bg-yellow-500 text-white text-sm font-medium rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {saving && <RefreshCw size={14} className="animate-spin" />}
              Reschedule
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Outcome Modal (any legal forward transition, reason when required) ───────

function OutcomeModal({ trial, onConfirm, onClose, saving }) {
  const options = getAllowedTransitions(trial.status).filter(s => s !== 'expired');
  const [status, setStatus] = useState(options[0] ?? '');
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);

  function handleConfirm() {
    if (!status) { setError('Select an outcome.'); return; }
    if (requiresOutcomeReason(status) && !reason.trim()) {
      setError(`A reason is required for ${TRIAL_STATUS_LABELS[status]}.`);
      return;
    }
    onConfirm(status, reason.trim() || null);
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">Record Outcome</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={18} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">{trial.studentName} — currently <StatusBadge status={trial.status} /></p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">New status</label>
            <div className="space-y-1.5">
              {options.map(o => (
                <label key={o} className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="outcome" checked={status === o} onChange={() => { setStatus(o); setError(null); }}
                    className="text-purple-600 focus:ring-purple-500" />
                  <span className="text-sm text-gray-700">{TRIAL_STATUS_LABELS[o]}</span>
                  {o === 'won' && <span className="text-xs text-amber-600">(records an off-platform payment — does not charge the customer)</span>}
                </label>
              ))}
            </div>
          </div>

          {requiresOutcomeReason(status) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason <span className="text-red-500">*</span></label>
              <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none" />
            </div>
          )}

          {error && <p className="text-xs text-red-500">{error}</p>}

          <div className="flex gap-3">
            <button onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button onClick={handleConfirm} disabled={saving || !status}
              className="flex-1 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {saving && <RefreshCw size={14} className="animate-spin" />}
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Override Modal (bypasses the status machine, logged distinctly) ──────────

function OverrideModal({ trial, onConfirm, onClose, saving }) {
  const [status, setStatus] = useState(trial.status);
  const [confirmed, setConfirmed] = useState(false);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">Override Status</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={18} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
            <AlertTriangle size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-700">
              This bypasses the normal status flow. Use it to correct a mistake, not for routine transitions.
              Logged distinctly in the trial's history.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select value={status} onChange={e => { setStatus(e.target.value); setConfirmed(false); }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500">
              {TRIAL_STATUSES.map(s => (
                <option key={s} value={s}>{TRIAL_STATUS_LABELS[s]}</option>
              ))}
            </select>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)}
              className="rounded border-amber-400 text-amber-600" />
            <span className="text-xs text-amber-800">I understand this skips the normal flow</span>
          </label>

          <div className="flex gap-3">
            <button onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button onClick={() => onConfirm(status)} disabled={saving || !confirmed || status === trial.status}
              className="flex-1 px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {saving && <RefreshCw size={14} className="animate-spin" />}
              Override
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Delete Modal (trial only, or trial + courseless child on a real account) ─
// Replaces the plain window.confirm so an admin can choose whether to also
// remove the child entry a converted-to-account trial left on the parent's
// users doc. For admin-created trials (userId null) only "Delete trial only"
// shows — same as before, just in a modal.

function DeleteTrialModal({ context, allTrials, onConfirm, onClose, saving }) {
  const isGroup = context.mode === 'group';
  const items = isGroup ? context.siblings : [context.trial];
  const names = items.map(t => t.studentName).join(', ');

  // Which of these trials sit on a real parent account and point at a child
  // entry (trial.studentId). Only these are eligible for child removal.
  const accountLinked = items.filter(t => t.userId && t.studentId);
  const hasAccountLinked = accountLinked.length > 0;
  const removeLabel = accountLinked.length === 1
    ? `Delete & remove ${accountLinked[0].studentName} from the account`
    : `Delete & remove ${accountLinked.length} children from the account`;

  // Orphan-risk check (client-side, no extra read — allTrials is the page's
  // full list). For each linked trial being deleted: if the deletion leaves
  // that account with NO trials at all, and staff pick "delete trial only",
  // the courseless child it points at is stranded — nothing to enrol from,
  // /trial-status renders its empty state, and ProtectedRoute keeps routing
  // them back to it. Recommend "delete & remove the child"; don't force it.
  const deletedIds = new Set(items.map(t => t.id));
  const trialsList = Array.isArray(allTrials) ? allTrials : [];
  const orphanRiskNames = accountLinked
    .filter(t => trialsList.filter(x => x.userId === t.userId && !deletedIds.has(x.id)).length === 0)
    .map(t => t.studentName);
  const orphanRisk = orphanRiskNames.length > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">
            {isGroup ? `Delete ${items.length} trials` : 'Delete trial'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            {isGroup
              ? `Students: ${names}`
              : `${context.trial.studentName} — ${context.trial.parentName}`}
          </p>

          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
            <Info size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-700">
              For a real signup, marking the trial <strong>Cancelled</strong> usually
              beats deleting it — deletion loses the funnel record. Delete is for
              junk entries.
            </p>
          </div>

          {hasAccountLinked && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
              <AlertTriangle size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-red-700">
                {accountLinked.length === 1 && !isGroup
                  ? <>This parent has an account. <strong>{context.trial.studentName}</strong> sits on it as a trial entry (no course).</>
                  : <><strong>{accountLinked.length}</strong> of these students sit on a parent account as trial entries (no course).</>}
                {' '}A child with a course is never touched.
              </p>
            </div>
          )}

          {orphanRisk && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2.5">
              <AlertTriangle size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-800">
                {orphanRiskNames.length === 1
                  ? <>This is the account's only trial. Delete <strong>trial only</strong> and{' '}
                      <strong>{orphanRiskNames[0]}</strong> is left as a courseless child on the account with
                      nothing to enrol from — the parent's trial status page will be empty and keep sending
                      them back to it.</>
                  : <>These are the last trials on their accounts. Delete <strong>trials only</strong> and{' '}
                      <strong>{orphanRiskNames.join(', ')}</strong> are left as courseless children with
                      nothing to enrol from and an empty trial status page.</>}
                {' '}Use <strong>“{removeLabel}”</strong> below instead, unless you have a reason to keep the record.
              </p>
            </div>
          )}

          <div className="flex flex-col gap-2 pt-1">
            <button
              onClick={() => onConfirm(false)}
              disabled={saving}
              className="w-full px-4 py-2 bg-gray-700 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving && <RefreshCw size={14} className="animate-spin" />}
              {isGroup ? `Delete ${items.length} trials only` : 'Delete trial only'}
            </button>

            {hasAccountLinked && (
              <button
                onClick={() => onConfirm(true)}
                disabled={saving}
                className="w-full px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving && <RefreshCw size={14} className="animate-spin" />}
                {removeLabel}
              </button>
            )}

            <button
              onClick={onClose}
              disabled={saving}
              className="w-full px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Link to Account Modal (Case B / C2 — repair an unlinked trial) ──────────
// Reached from the "Link to account" button on any trial row with userId null.
// The parent lookup has already run by the time this renders:
//   status 'not-found' → no users doc matches the trial email; nothing to do
//   status 'found'     → `account` is the match; confirm before linking
// A child on that account already carrying the trial's student name (normalized)
// is the signature of a student who is on the account by another route already —
// linking would duplicate a child on a paying customer — so that case demands a
// second, separate confirmation (the nameClash checkbox) on top of the normal one.
function LinkAccountModal({ context, onConfirm, onClose, saving }) {
  const { trial, status, account, nameClash, linkBlockedReason } = context;
  const [clashAck, setClashAck] = useState(false);

  const enrolledCount = account
    ? (account.children || []).filter(c => c && c.course).length
    : 0;
  const holder = account
    ? ([account.firstName, account.lastName].filter(Boolean).join(' ') || account.email)
    : '';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">Link trial to an account</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            {trial.studentName} — {trial.parentName}
            <span className="text-gray-400 mx-1.5">·</span>
            {trial.parentEmail}
          </p>

          {status === 'loading' && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <RefreshCw size={14} className="animate-spin" /> Checking for an account…
            </div>
          )}

          {status === 'not-found' && (
            <>
              <div className="flex items-start gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
                <Info size={14} className="text-gray-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-gray-600">
                  No account exists for <strong>{trial.parentEmail}</strong>. There is nothing to link this
                  trial to. Send the parent your trial link so they can sign up, then link it once their
                  account exists.
                </p>
              </div>
              <button
                onClick={onClose}
                className="w-full px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </>
          )}

          {status === 'found' && account && (
            <>
              <div className="bg-purple-50 border border-purple-200 rounded-lg px-3 py-2.5 space-y-0.5">
                <p className="text-xs font-semibold text-purple-800">This email belongs to:</p>
                <p className="text-sm text-gray-800">{holder}</p>
                <p className="text-xs text-gray-500">{account.email}</p>
                <p className="text-xs text-gray-500">
                  {enrolledCount} enrolled {enrolledCount === 1 ? 'child' : 'children'}
                </p>
              </div>

              {linkBlockedReason ? (
                <>
                  <div className="flex items-start gap-2 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2.5">
                    <AlertTriangle size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-amber-800">
                      {linkBlockedReason === 'adult-account'
                        ? "This is an adult self-learner account — it already holds its one learner, the account holder. An adult wanting another course renews at /course-renewal; a child needs their own family account. This trial can't be linked here."
                        : "This is a family account and this trial is for an adult learner. An adult learner needs their own adult account — this trial can't be linked here."}
                    </p>
                  </div>
                  <button
                    onClick={onClose}
                    className="w-full px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                </>
              ) : (
              <>
              <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5">
                <Info size={14} className="text-blue-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-blue-700">
                  A courseless child entry for <strong>{trial.studentName}</strong> is added to this
                  account so the parent can see the trial and enrol. Their existing children and courses
                  are not touched.
                </p>
              </div>

              {nameClash && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-300 rounded-lg px-3 py-2.5">
                  <AlertTriangle size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
                  <div className="space-y-1.5">
                    <p className="text-xs text-red-700">
                      This account <strong>already has a child named "{trial.studentName}"</strong>. That is
                      usually the same student, already on the account by another route — linking would
                      create a duplicate child on a paying customer.
                    </p>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={clashAck}
                        onChange={e => setClashAck(e.target.checked)}
                        className="rounded border-red-400 text-red-600"
                      />
                      <span className="text-xs font-medium text-red-800">
                        I've checked — this is a different student
                      </span>
                    </label>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={onConfirm}
                  disabled={saving || (nameClash && !clashAck)}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving && <RefreshCw size={14} className="animate-spin" />}
                  Link to this account
                </button>
              </div>
              </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Trial Row ──────────────────────────────────────────────────────────────

function TrialRow({ trial, siblingCount, onSchedule, onReschedule, onOutcome, onQuickCancel, onMarkExpired, onOverride, onEdit, onDelete, onDeleteGroup, onLinkAccount, actionLoading }) {
  // A group delete in flight disables every sibling row, not just the one
  // clicked — prevents a race against a per-row delete on another sibling.
  const isLoading = actionLoading === trial.id || (!!trial.groupId && actionLoading === trial.groupId);
  const isPastDue = trial.status === 'scheduled' && trial.scheduledAt && new Date(trial.scheduledAt) <= new Date();
  const displayTz = getTrialTimezone(trial);
  // null for every admin-created trial that skipped DOB and every trial from
  // before it became required on the public form — that's the common case
  // here, not the edge case, so render nothing rather than "age null"/"age 0".
  //
  // Trial DOBs are stored at UTC midnight (deliberately — see
  // dateOnlyToISOString above; CreateAccount stores local midnight instead).
  // calculateAge compares against local "now", so a viewer west of UTC can
  // read the age one day behind for at most a day around a birthday — they'd
  // still see the old age on the morning of the birthday itself. Known,
  // accepted: do not "fix" this by changing the storage format.
  const age = calculateAge(trial.dateOfBirth);

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-4 flex-wrap">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <p className="font-semibold text-gray-800">
            {trial.studentName}
            {age !== null && <span className="text-gray-400 font-normal"> · {age}</span>}
          </p>
          <StatusBadge status={trial.status} />
          {trial.status === 'won' && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium inline-flex items-center gap-1">
              <DollarSign size={11} />Paid
            </span>
          )}
          {!trial.userId && (
            <span
              className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium inline-flex items-center gap-1"
              title="Not attached to a parent account — the parent can't see or pay for this trial until it's linked"
            >
              <Unlink size={11} />Unlinked
            </span>
          )}
          {trial.studentType === 'adult' && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-medium">
              Adult
            </span>
          )}
          {siblingCount > 1 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 font-medium inline-flex items-center gap-1">
              <Users size={11} />Sibling group ({siblingCount})
            </span>
          )}
          {trial.attribution?.code && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 font-medium">
              {trial.attribution.code}
            </span>
          )}
        </div>
        <p className="text-sm text-gray-700">
          {trial.parentName}
          <span className="text-gray-400 mx-1.5">·</span>
          <a href={`mailto:${trial.parentEmail}`} className="text-purple-600 hover:underline inline-flex items-center gap-1">
            <Mail size={11} />{trial.parentEmail}
          </a>
          {trial.parentPhone && (
            <>
              <span className="text-gray-400 mx-1.5">·</span>
              <span className="inline-flex items-center gap-1"><Phone size={11} />{trial.parentPhone}</span>
            </>
          )}
        </p>
        <p className="text-xs text-gray-400 mt-0.5">
          {trial.teacherName ? trial.teacherName : 'No teacher assigned'}
          {trial.scheduledAt && (
            <>
              <span className="mx-1.5">·</span>
              {formatISOForDisplay(trial.scheduledAt, displayTz ?? 'UTC')} on {isoToDisplayDate(trial.scheduledAt, displayTz ?? 'UTC')}
              {!displayTz && <span className="text-amber-500 ml-1">(timezone unresolved — shown in UTC, not the viewer's local time)</span>}
              {isPastDue && <span className="text-orange-500 ml-1 font-medium">· past due</span>}
            </>
          )}
          {trial.meetingUrl && (
            <>
              <span className="mx-1.5">·</span>
              <a href={trial.meetingUrl} target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline inline-flex items-center gap-1">
                <LinkIcon size={11} />Zoom
              </a>
            </>
          )}
        </p>
        {/* Only shown while it still describes the CURRENT status — a reschedule
            deliberately leaves outcomeReason on the document (it's the only
            record of why a no-show happened), so a stale reason must not
            display next to a status it no longer explains. */}
        {trial.outcomeReason && requiresOutcomeReason(trial.status) && (
          <p className="text-xs text-gray-400 mt-0.5 italic">"{trial.outcomeReason}"</p>
        )}
        <p className="text-xs text-gray-300 mt-0.5">Logged {formatDateShort(trial.createdAt)}</p>
      </div>

      <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto sm:flex-shrink-0">
        {trial.status === 'requested' && (
          <>
            <button onClick={() => onSchedule(trial)} disabled={isLoading}
              className="flex items-center gap-1.5 px-3 py-2 bg-purple-600 text-white text-xs font-medium rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50">
              <Calendar size={13} />Schedule
            </button>
            <button onClick={() => onMarkExpired(trial)} disabled={isLoading}
              className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 text-gray-500 text-xs font-medium rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50">
              <Clock size={13} />Mark Expired
            </button>
          </>
        )}
        {trial.status === 'scheduled' && isPastDue && (
          <button onClick={() => onOutcome(trial)} disabled={isLoading}
            className="flex items-center gap-1.5 px-3 py-2 bg-purple-600 text-white text-xs font-medium rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50">
            <CheckCircle size={13} />Record Outcome
          </button>
        )}
        {trial.status === 'scheduled' && !isPastDue && (
          <button onClick={() => onQuickCancel(trial)} disabled={isLoading}
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 text-gray-500 text-xs font-medium rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50">
            <Ban size={13} />Cancel
          </button>
        )}
        {(trial.status === 'held' || trial.status === 'no_show') && (
          <button onClick={() => onOutcome(trial)} disabled={isLoading}
            className="flex items-center gap-1.5 px-3 py-2 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50">
            <Award size={13} />Won / Lost
          </button>
        )}
        {['scheduled', 'no_show', 'cancelled'].includes(trial.status) && (
          <button onClick={() => onReschedule(trial)} disabled={isLoading}
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-yellow-300 text-yellow-700 text-xs font-medium rounded-lg hover:bg-yellow-50 transition-colors disabled:opacity-50">
            <Calendar size={13} />Reschedule
          </button>
        )}
        {/* convertedPackageId means money moved through this trial — it's already
            on an account, and linkTrialToAccount would only ever refuse it. */}
        {!trial.userId && !trial.convertedPackageId && (
          <button onClick={() => onLinkAccount(trial)} disabled={isLoading}
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50">
            <LinkIcon size={13} />Link to account
          </button>
        )}
        <button onClick={() => onEdit(trial)} disabled={isLoading}
          className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors" title="Edit">
          <Edit2 size={15} />
        </button>
        <button onClick={() => onOverride(trial)} disabled={isLoading}
          className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors" title="Override status">
          <RotateCcw size={15} />
        </button>
        <button onClick={() => onDelete(trial)} disabled={isLoading}
          className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
          {isLoading ? <RefreshCw size={15} className="animate-spin" /> : <Trash2 size={15} />}
        </button>
        {siblingCount > 1 && (
          <button onClick={() => onDeleteGroup(trial.groupId)} disabled={isLoading}
            className="px-2.5 py-1.5 text-xs font-medium text-red-500 border border-red-200 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
            title="Delete every trial in this sibling group">
            Delete group
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

function TrialManager() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [trials, setTrials] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [referralCodes, setReferralCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Filters — drive both the stat tiles and the section lists
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [teacherFilter, setTeacherFilter] = useState('');
  const [codeFilter, setCodeFilter] = useState('');

  // Modals
  const [formModal, setFormModal] = useState(null);       // null | { mode: 'add' } | { mode: 'edit', trial }
  const [scheduleModal, setScheduleModal] = useState(null); // null | trial
  const [rescheduleModal, setRescheduleModal] = useState(null); // null | trial
  const [outcomeModal, setOutcomeModal] = useState(null);   // null | trial
  const [overrideModal, setOverrideModal] = useState(null); // null | trial
  const [deleteModal, setDeleteModal] = useState(null); // null | { mode:'single', trial } | { mode:'group', groupId, siblings }
  const [linkModal, setLinkModal] = useState(null); // null | { trial, status:'loading'|'not-found'|'found', account, nameClash }
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState(null); // trial id, or a groupId during a group delete
  const [saveResult, setSaveResult] = useState(null); // null | { succeeded: [{key,studentName}], failed: [{key,studentName,message}] }

  function showSuccess(msg) {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 4000);
  }

  const fetchAll = async () => {
    try {
      setLoading(true);
      setError(null);
      const [trialSnap, teacherSnap, codeSnap] = await Promise.all([
        getDocs(query(collection(db, 'trials'), orderBy('createdAt', 'desc'))),
        getDocs(query(collection(db, 'teachers'), orderBy('name'))),
        getDocs(collection(db, 'referralCodes')),
      ]);
      setTrials(trialSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setTeachers(teacherSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setReferralCodes(codeSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('TrialManager fetch error:', err);
      setError('Failed to load data. Please refresh.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // ── Filtering + funnel stats (single source for tiles and lists) ────────────

  const filters = useMemo(() => ({
    startDate: startDate ? new Date(startDate + 'T00:00:00') : null,
    endDate: endDate ? new Date(endDate + 'T23:59:59') : null,
    teacherId: teacherFilter || null,
    code: codeFilter || null,
  }), [startDate, endDate, teacherFilter, codeFilter]);

  const stats = useMemo(() => computeTrialFunnelStats(trials, filters), [trials, filters]);
  const filtered = stats.filtered;

  const now = new Date();
  const isFutureSlot = (t) => t.scheduledAt && new Date(t.scheduledAt) > now;
  const needsScheduling = filtered.filter(t => t.status === 'requested');
  // PHASE 2 — a `won` trial keeps its scheduledAt: if the parent paid before the
  // trial happened, that booked session becomes their first lesson and staff
  // must still see it here. Upcoming = a future slot that is either still a
  // trial (`scheduled`) or already paid (`won`).
  const upcoming = filtered.filter(t => (t.status === 'scheduled' || t.status === 'won') && isFutureSlot(t));
  const needsOutcome = filtered.filter(t => t.status === 'scheduled' && !isFutureSlot(t));
  const recent = filtered.filter(t =>
    ['held', 'won', 'lost', 'no_show', 'cancelled', 'expired'].includes(t.status)
    // a paid trial with a future slot lives in Upcoming, not here
    && !(t.status === 'won' && isFutureSlot(t)));

  // How many trials in the current view share each groupId — siblings can land
  // in different sections (different live statuses), so this is a per-row badge
  // rather than visual nesting, which wouldn't hold up once they split apart.
  const groupCounts = useMemo(() => {
    const counts = {};
    filtered.forEach(t => {
      if (t.groupId) counts[t.groupId] = (counts[t.groupId] ?? 0) + 1;
    });
    return counts;
  }, [filtered]);

  // ── Save (create or edit) ─────────────────────────────────────────────────────
  // Edit always touches exactly one existing doc's fields — status/statusHistory
  // untouched. Create may produce several docs (one per student in form.students),
  // each a full trial record sharing every parent-level field plus a common
  // groupId when there's more than one — see the form's own doc comment.

  const handleSaveTrial = async (form) => {
    setSaving(true);
    setSaveResult(null);
    try {
      // Re-derive from the submitted intake location rather than trusting a
      // client-side zone value — same rule the form's live label used. Same
      // zone applies to every student row (location is parent-level).
      const effectiveTz = resolveTimezone(form.intake.country, form.intake.stateProvince) ?? 'UTC';
      const nowISO = new Date().toISOString();

      const sharedIntake = {
        city: form.intake.city?.trim() || null,
        stateProvince: form.intake.stateProvince?.trim() || null,
        country: form.intake.country?.trim() || null,
        preferredTimes: form.intake.preferredTimes?.trim() || null,
        notes: form.intake.notes?.trim() || null,
      };

      const sharedFields = {
        parentName: form.parentName.trim(),
        parentEmail: form.parentEmail.trim(),
        parentPhone: form.parentPhone?.trim() || null,
        attribution: {
          code: form.attribution.code || null,
          codeType: referralCodes.find(c => c.id === form.attribution.code)?.type ?? null,
          heardAbout: form.attribution.heardAbout || null,
          heardAboutOther: form.attribution.heardAbout === 'Other' ? (form.attribution.heardAboutOther?.trim() || null) : null,
          fbclid: null,
          referrer: null,
          landingPath: null,
        },
        updatedAt: nowISO,
      };

      if (form.id) {
        // Edit — this trial's own fields only. studentType/groupId are creation-
        // time-only and never touched here. Single row, its own schedule
        // fields directly (no copy-down concept in edit mode).
        const student = form.students[0];
        const studentName = form.isAdult ? form.parentName.trim() : student.studentName.trim();
        const teacher = teachers.find(t => t.id === student.teacherId);
        const hasSchedule = !!(student.teacherId && student.dateStr && student.timeStr);
        const scheduledAt = hasSchedule ? localDateTimeToUTCISO(student.dateStr, student.timeStr, effectiveTz) : null;
        await updateDoc(doc(db, 'trials', form.id), {
          ...sharedFields,
          studentName,
          dateOfBirth: dateOnlyToISOString(student.dateOfBirth),
          teacherId: student.teacherId || null,
          teacherName: teacher?.name ?? null,
          scheduledAt,
          durationMins: student.durationMins || DEFAULT_TRIAL_DURATION_MINS,
          meetingUrl: student.meetingUrl?.trim() || null,
          intake: {
            ...sharedIntake,
            arabicLevel: student.arabicLevel?.trim() || null,
            dialect: student.dialect || null,
          },
        });
        showSuccess(`${studentName} updated.`);
        setFormModal(null);
        await fetchAll();
      } else {
        const by = user?.uid ?? null;
        const first = form.students[0];
        const rawStudents = form.isAdult
          ? [{ ...first, studentName: form.parentName.trim() }]
          : form.students;
        // Null for a single-student submission — only stamped when siblings
        // are actually being created together in this same submission.
        const groupId = rawStudents.length > 1
          ? `grp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
          : null;

        const results = await Promise.allSettled(rawStudents.map(async (student) => {
          // Copy-down: a ticked non-first row inherits row 0's CURRENT schedule
          // fields; row 0 itself and any unticked row use its own. Adult mode
          // never has a "first row" relationship since it's always one row.
          const usesFirstRowSchedule = !form.isAdult && student.key !== first.key && student.sameAsFirst;
          const eff = usesFirstRowSchedule
            ? { teacherId: first.teacherId, dateStr: first.dateStr, timeStr: first.timeStr, durationMins: first.durationMins, meetingUrl: first.meetingUrl }
            : { teacherId: student.teacherId, dateStr: student.dateStr, timeStr: student.timeStr, durationMins: student.durationMins, meetingUrl: student.meetingUrl };

          const teacher = teachers.find(t => t.id === eff.teacherId);
          // Per-row, not per-submission: siblings can legitimately end up in
          // different statuses depending on whether THEIR OWN slot was filled in.
          const hasSchedule = !!(eff.teacherId && eff.dateStr && eff.timeStr);
          const scheduledAt = hasSchedule ? localDateTimeToUTCISO(eff.dateStr, eff.timeStr, effectiveTz) : null;
          const status = hasSchedule ? 'scheduled' : 'requested';
          // Creating straight into 'scheduled' still passes through 'requested'
          // in history — funnel math over statusHistory can reconstruct the
          // full path even though the live status snapshot skips it.
          // Built via appendTrialStatusHistory so admin-created and self-signup
          // (createTrialSignup) trials write the identical 5-field entry shape
          // { status, changedAt, source, by, detail } (recon D6).
          let statusHistory = appendTrialStatusHistory({ statusHistory: [] }, 'requested', by, 'create');
          if (hasSchedule) {
            statusHistory = appendTrialStatusHistory({ statusHistory }, 'scheduled', by, 'create_scheduled');
          }
          const newTrial = {
            userId: null,
            // Maps to users.children[].id once a trial converts and a real
            // child record exists — always null for an admin-created lead.
            studentId: null,
            studentType: form.isAdult ? 'adult' : 'child',
            studentName: student.studentName.trim(),
            dateOfBirth: dateOnlyToISOString(student.dateOfBirth),
            groupId,
            ...sharedFields,
            teacherId: eff.teacherId || null,
            teacherName: teacher?.name ?? null,
            scheduledAt,
            durationMins: eff.durationMins || DEFAULT_TRIAL_DURATION_MINS,
            meetingUrl: eff.meetingUrl?.trim() || null,
            intake: {
              ...sharedIntake,
              arabicLevel: student.arabicLevel?.trim() || null,
              dialect: student.dialect || null,
            },
            status,
            outcomeReason: null,
            convertedAt: null,
            convertedPackageId: null,
            createdAt: nowISO,
            createdBy: 'admin',
            statusHistory,
          };
          const ref = await addDoc(collection(db, 'trials'), newTrial);

          // Case B (C1): if staff ticked "link to their account", attach this
          // freshly-created trial to it. A link failure is NOT a create failure
          // — the trial doc exists — so it's caught here and reported, never
          // rethrown (a rethrow would mark the row failed and a retry would
          // create a SECOND trial doc). Staff finish an un-linked one with
          // "Link to account" on the row (C2).
          let linkResult = null;
          if (form.linkTargetUserId) {
            try {
              linkResult = await linkTrialToAccount(
                { id: ref.id, ...newTrial },
                { id: form.linkTargetUserId },
                by,
              );
            } catch (linkErr) {
              console.error('linkTrialToAccount threw during create for', ref.id, linkErr);
              linkResult = { linked: false, reason: 'link-threw', childId: null };
            }
          }
          return { key: student.key, studentName: student.studentName.trim(), linkResult };
        }));

        const succeeded = [];
        const failed = [];
        results.forEach((r, i) => {
          const student = rawStudents[i];
          if (r.status === 'fulfilled') {
            succeeded.push({ key: student.key, studentName: student.studentName.trim(), linkResult: r.value.linkResult });
          } else {
            failed.push({ key: student.key, studentName: student.studentName.trim(), message: r.reason?.message || 'Unknown error' });
          }
        });

        // Link outcomes on the trials that DID save (C1). linkResult is null
        // when staff didn't ask to link.
        const linkedOk = succeeded.filter(s => s.linkResult?.linked);
        const linkIssues = succeeded.filter(s => s.linkResult && !s.linkResult.linked);
        const linkedNote = linkedOk.length > 0
          ? ` Linked ${linkedOk.length === 1 ? 'it' : `${linkedOk.length}`} to the account.`
          : '';
        const linkIssueNote = linkIssues.length > 0
          ? ` Created but not linked: ${linkIssues
              .map(s => `${s.studentName} (${LINK_REASON_TEXT[s.linkResult.reason] ?? s.linkResult.reason})`)
              .join('; ')}. Use "Link to account" on the row.`
          : '';

        if (failed.length === 0) {
          showSuccess((succeeded.length > 1
            ? `${succeeded.length} trials logged for ${sharedFields.parentName}.`
            : `${succeeded[0].studentName} logged.`) + linkedNote);
          if (linkIssueNote) setError(linkIssueNote.trim());
          setFormModal(null);
        } else {
          // Some siblings already saved — never re-create them. Keep the modal
          // open (TrialFormModal reacts to saveResult and trims its own form
          // down to just the failed rows) and report exactly what happened.
          setSaveResult({ succeeded, failed });
          if (succeeded.length > 0) {
            showSuccess(`Saved: ${succeeded.map(s => s.studentName).join(', ')}.${linkedNote}`);
          }
          setError(`Couldn't save ${failed.map(f => f.studentName).join(', ')} — see the form for details.${linkIssueNote}`);
        }
        await fetchAll();
      }
    } catch (err) {
      console.error('Save trial error:', err);
      setError('Failed to save trial. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Schedule (requested → scheduled) ──────────────────────────────────────────

  const handleScheduleConfirm = async ({ teacherId, scheduledAtISO, durationMins, meetingUrl }) => {
    const trial = scheduleModal;
    setSaving(true);
    try {
      const teacher = teachers.find(t => t.id === teacherId);
      await updateDoc(doc(db, 'trials', trial.id), {
        teacherId,
        teacherName: teacher?.name ?? null,
        scheduledAt: scheduledAtISO,
        durationMins,
        meetingUrl,
        status: 'scheduled',
        statusHistory: appendTrialStatusHistory(trial, 'scheduled', user?.uid ?? null, 'schedule'),
        updatedAt: new Date().toISOString(),
        ...conversionFieldsForStatusChange(trial, 'scheduled'),
      });
      showSuccess(`${trial.studentName} scheduled.`);
      setScheduleModal(null);
      await fetchAll();
    } catch (err) {
      console.error('Schedule trial error:', err);
      setError('Failed to schedule trial.');
    } finally {
      setSaving(false);
    }
  };

  // ── Reschedule (scheduled/no_show/cancelled → scheduled) ─────────────────────
  // Never touches outcomeReason, groupId, or studentType — only teacher/schedule
  // fields, status, statusHistory, updatedAt, and (via
  // conversionFieldsForStatusChange) a stale payment-less convertedAt if one is
  // hanging around. Affects only the one document being rescheduled.

  const handleRescheduleConfirm = async ({ teacherId, scheduledAtISO, durationMins, meetingUrl }) => {
    const trial = rescheduleModal;
    setSaving(true);
    try {
      const teacher = teachers.find(t => t.id === teacherId);
      // Same zone applies to both old and new times — location doesn't change
      // on a reschedule, only the slot does.
      const tz = getTrialTimezone(trial) ?? 'UTC';
      const oldWhen = trial.scheduledAt
        ? `${isoToDisplayDate(trial.scheduledAt, tz)} ${isoToDisplayTime(trial.scheduledAt, tz)} ${tz}`
        : 'unscheduled';
      const newWhen = `${isoToDisplayDate(scheduledAtISO, tz)} ${isoToDisplayTime(scheduledAtISO, tz)} ${tz}`;
      const detail = `${oldWhen} → ${newWhen}, teacher ${trial.teacherName ?? 'none'} → ${teacher?.name ?? 'none'}`;

      await updateDoc(doc(db, 'trials', trial.id), {
        teacherId,
        teacherName: teacher?.name ?? null,
        scheduledAt: scheduledAtISO,
        durationMins,
        meetingUrl,
        status: 'scheduled',
        statusHistory: appendTrialStatusHistory(trial, 'scheduled', user?.uid ?? null, 'reschedule', detail),
        updatedAt: new Date().toISOString(),
        ...conversionFieldsForStatusChange(trial, 'scheduled'),
      });
      showSuccess(`${trial.studentName} rescheduled.`);
      setRescheduleModal(null);
      await fetchAll();
    } catch (err) {
      console.error('Reschedule trial error:', err);
      setError('Failed to reschedule trial.');
    } finally {
      setSaving(false);
    }
  };

  // ── Outcome (any legal forward transition) ────────────────────────────────────

  const handleOutcomeConfirm = async (newStatus, reason) => {
    const trial = outcomeModal;
    setSaving(true);
    try {
      const payload = {
        status: newStatus,
        statusHistory: appendTrialStatusHistory(trial, newStatus, user?.uid ?? null, 'outcome'),
        outcomeReason: requiresOutcomeReason(newStatus) ? reason : null,
        updatedAt: new Date().toISOString(),
        ...conversionFieldsForStatusChange(trial, newStatus),
      };
      if (newStatus === 'won') payload.convertedAt = new Date().toISOString();
      await updateDoc(doc(db, 'trials', trial.id), payload);
      showSuccess(`${trial.studentName} marked ${TRIAL_STATUS_LABELS[newStatus]}.`);
      setOutcomeModal(null);
      await fetchAll();
    } catch (err) {
      console.error('Outcome trial error:', err);
      setError('Failed to record outcome.');
    } finally {
      setSaving(false);
    }
  };

  // ── Quick cancel (scheduled, still upcoming) ──────────────────────────────────

  const handleQuickCancel = async (trial) => {
    if (!window.confirm(`Cancel ${trial.studentName}'s trial?`)) return;
    setActionLoading(trial.id);
    try {
      await updateDoc(doc(db, 'trials', trial.id), {
        status: 'cancelled',
        statusHistory: appendTrialStatusHistory(trial, 'cancelled', user?.uid ?? null, 'outcome'),
        updatedAt: new Date().toISOString(),
        ...conversionFieldsForStatusChange(trial, 'cancelled'),
      });
      showSuccess(`${trial.studentName} cancelled.`);
      await fetchAll();
    } catch (err) {
      console.error('Cancel trial error:', err);
      setError('Failed to cancel trial.');
    } finally {
      setActionLoading(null);
    }
  };

  // ── Mark expired (requested, stale) ───────────────────────────────────────────

  const handleMarkExpired = async (trial) => {
    if (!window.confirm(`Mark ${trial.studentName}'s trial request as expired?`)) return;
    setActionLoading(trial.id);
    try {
      await updateDoc(doc(db, 'trials', trial.id), {
        status: 'expired',
        statusHistory: appendTrialStatusHistory(trial, 'expired', user?.uid ?? null, 'outcome'),
        updatedAt: new Date().toISOString(),
        ...conversionFieldsForStatusChange(trial, 'expired'),
      });
      showSuccess(`${trial.studentName} marked expired.`);
      await fetchAll();
    } catch (err) {
      console.error('Expire trial error:', err);
      setError('Failed to mark trial expired.');
    } finally {
      setActionLoading(null);
    }
  };

  // ── Override ───────────────────────────────────────────────────────────────

  const handleOverrideConfirm = async (newStatus) => {
    const trial = overrideModal;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'trials', trial.id), {
        status: newStatus,
        statusHistory: appendTrialStatusHistory(trial, newStatus, user?.uid ?? null, 'override'),
        updatedAt: new Date().toISOString(),
        ...conversionFieldsForStatusChange(trial, newStatus),
      });
      showSuccess(`${trial.studentName} status overridden to ${TRIAL_STATUS_LABELS[newStatus]}.`);
      setOverrideModal(null);
      await fetchAll();
    } catch (err) {
      console.error('Override trial error:', err);
      setError('Failed to override status.');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────────────
  // Both single and group delete go through DeleteTrialModal so the admin can
  // choose whether to also remove a courseless child entry that a
  // converted-to-account trial left on the parent's users doc. See
  // removeCourselessTrialChildren for the guards on that write.

  const handleDelete = (trial) => {
    setDeleteModal({ mode: 'single', trial });
  };

  // Looks up siblings from the full, unfiltered `trials` — deleting "the whole
  // group" shouldn't silently spare a sibling just because an active filter
  // currently hides it from view.
  const handleDeleteGroup = (groupId) => {
    const siblings = trials.filter(t => t.groupId === groupId);
    if (siblings.length === 0) return;
    setDeleteModal({ mode: 'group', groupId, siblings });
  };

  // removeChildren === true only when the admin clicked the "…remove from the
  // account" button, which DeleteTrialModal shows only when at least one item
  // has userId + studentId.
  const handleDeleteConfirm = async (removeChildren) => {
    const ctx = deleteModal;
    if (!ctx) return;
    setSaving(true);
    try {
      const items = ctx.mode === 'group' ? ctx.siblings : [ctx.trial];

      // 1. Delete the trial doc(s).
      const results = await Promise.allSettled(
        items.map(t => deleteDoc(doc(db, 'trials', t.id)))
      );
      const failedNames = results
        .map((r, i) => (r.status === 'rejected' ? items[i].studentName : null))
        .filter(Boolean);

      // 2. Optionally remove the courseless child entry each trial points at,
      //    grouped by parent account (one transaction per userId).
      let childrenRemoved = 0;
      let childrenKeptWithCourse = 0;
      if (removeChildren) {
        const idsByUser = {};
        items.forEach(t => {
          if (t.userId && t.studentId) {
            (idsByUser[t.userId] = idsByUser[t.userId] || []).push(t.studentId);
          }
        });
        for (const [userId, studentIds] of Object.entries(idsByUser)) {
          try {
            const res = await removeCourselessTrialChildren(userId, studentIds);
            childrenRemoved += res.removed;
            childrenKeptWithCourse += res.keptWithCourse;
            if (res.reason && res.reason !== 'no-courseless-target' && res.reason !== 'nothing-to-do') {
              console.warn('removeCourselessTrialChildren:', userId, res);
            }
          } catch (childErr) {
            console.error('removeCourselessTrialChildren failed for', userId, childErr);
          }
        }
      }

      // 3. Report.
      const deletedCount = items.length - failedNames.length;
      let msg = `Deleted ${deletedCount} trial${deletedCount === 1 ? '' : 's'}.`;
      if (childrenRemoved > 0) msg += ` Removed ${childrenRemoved} child ${childrenRemoved === 1 ? 'entry' : 'entries'} from the account.`;
      if (childrenKeptWithCourse > 0) msg += ` ${childrenKeptWithCourse} child ${childrenKeptWithCourse === 1 ? 'entry' : 'entries'} had a course and ${childrenKeptWithCourse === 1 ? 'was' : 'were'} left untouched.`;
      if (failedNames.length > 0) {
        setError(`Some trials could not be deleted: ${failedNames.join(', ')}. Try again.`);
      } else {
        showSuccess(msg);
      }

      setDeleteModal(null);
      await fetchAll();
    } catch (err) {
      console.error('Delete trial error:', err);
      setError('Failed to delete. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Link to account (Case B / C2) ────────────────────────────────────────────
  // Opens the modal in a loading state, does a one-shot users read + client
  // match (case-insensitive, trimmed — never a where('email','==') query, which
  // misses mixed-case CreateAccount accounts), then hands off to
  // LinkAccountModal for the confirmation. The write is linkTrialToAccount,
  // which re-checks everything in a transaction — see its guards.

  const handleLinkToAccount = async (trial) => {
    setLinkModal({ trial, status: 'loading', account: null, nameClash: false });
    try {
      const snap = await getDocs(collection(db, 'users'));
      const e = normalizeEmail(trial.parentEmail);
      const account = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .find(u => normalizeEmail(u.email) === e) || null;
      if (!account) {
        setLinkModal({ trial, status: 'not-found', account: null, nameClash: false });
        return;
      }
      const targetName = (trial.studentName || '').trim().toLowerCase();
      const nameClash = targetName !== '' && (account.children || [])
        .some(c => c && (c.name || '').trim().toLowerCase() === targetName);
      // Item 4 — when linking is refused. `=== 'adult'` on each side; a legacy
      // account with no accountType is a family account. linkTrialToAccount
      // refuses these too; the modal shows the explanation instead of a button.
      //   'adult-account'  — target is an adult account: it already holds its
      //     one learner, whatever this trial's type (adult renews at
      //     /course-renewal, a child needs their own family account).
      //   'type-mismatch'  — family account + adult trial.
      const linkBlockedReason = account.accountType === 'adult'
        ? 'adult-account'
        : trial.studentType === 'adult'
          ? 'type-mismatch'
          : null;
      setLinkModal({ trial, status: 'found', account, nameClash, linkBlockedReason });
    } catch (err) {
      console.error('Link lookup error:', err);
      setError('Could not check for an account. Try again.');
      setLinkModal(null);
    }
  };

  const handleLinkConfirm = async () => {
    const ctx = linkModal;
    if (!ctx || ctx.status !== 'found') return;
    setSaving(true);
    try {
      const res = await linkTrialToAccount(ctx.trial, ctx.account, user?.uid ?? null);
      if (res.linked) {
        const holder = [ctx.account.firstName, ctx.account.lastName].filter(Boolean).join(' ')
          || ctx.account.email;
        showSuccess(`${ctx.trial.studentName} linked to ${holder}'s account.`);
        setLinkModal(null);
        await fetchAll();
      } else {
        setError(`Couldn't link: ${LINK_REASON_TEXT[res.reason] ?? res.reason}.`);
      }
    } catch (err) {
      console.error('Link trial error:', err);
      setError('Failed to link. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading trials...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto p-6 space-y-5">

        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin')} className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Trials</h1>
              <p className="text-sm text-gray-500">{stats.total} trial{stats.total !== 1 ? 's' : ''} in view</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchAll}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50 transition-colors">
              <RefreshCw size={15} />Refresh
            </button>
            <button onClick={() => { setSaveResult(null); setFormModal({ mode: 'add' }); }}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-xl hover:bg-purple-700 transition-colors">
              <Plus size={16} />Log a Trial
            </button>
          </div>
        </div>

        {/* Flash messages */}
        {successMessage && (
          <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
            <CheckCircle size={16} />{successMessage}
          </div>
        )}
        {error && (
          <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            <AlertTriangle size={16} />{error}
            <button onClick={() => setError(null)} className="ml-auto"><X size={14} /></button>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-end gap-3 flex-wrap">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Teacher</label>
            <select value={teacherFilter} onChange={e => setTeacherFilter(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500">
              <option value="">All teachers</option>
              {teachers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Referral code</label>
            <select value={codeFilter} onChange={e => setCodeFilter(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500">
              <option value="">All codes</option>
              {referralCodes.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
          {(startDate || endDate || teacherFilter || codeFilter) && (
            <button
              onClick={() => { setStartDate(''); setEndDate(''); setTeacherFilter(''); setCodeFilter(''); }}
              className="text-xs text-gray-400 hover:text-gray-600 underline pb-2.5"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Funnel stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {FUNNEL_TILES.map(s => (
            <div key={s} className="bg-white rounded-xl border border-gray-200 p-4">
              <p className="text-2xl font-bold text-gray-800">{stats.counts[s]}</p>
              <p className="text-xs text-gray-500 mt-0.5">{TRIAL_STATUS_LABELS[s]}</p>
            </div>
          ))}
        </div>
        {/* Stage 5 audit — two conversion rates, deliberately kept apart. See
            the long comment on computeTrialFunnelStats in trialHelper.js for
            why they can disagree and why neither is wrong. */}
        <div className="flex items-center gap-4 flex-wrap text-xs text-gray-500">
          <span>Requested → Won: <strong className="text-gray-700">{stats.requestedToWonRate !== null ? `${Math.round(stats.requestedToWonRate * 100)}%` : '—'}</strong></span>
          <span>No-show rate: <strong className="text-gray-700">{stats.noShowRate !== null ? `${Math.round(stats.noShowRate * 100)}%` : '—'}</strong></span>
          <span>
            Held → Won: <strong className="text-gray-700">{stats.heldToWonRate !== null ? `${Math.round(stats.heldToWonRate * 100)}%` : '—'}</strong>
            {stats.wonWithoutHeld > 0 && (
              <span className="text-gray-400"> ({stats.wonWithoutHeld} converted without a recorded trial session — why this can read over 100%)</span>
            )}
          </span>
          <span className="flex items-center gap-1 text-amber-600">
            <Info size={12} />
            "Won" is set by a completed payment, or by hand for an off-platform one — payment is never gated on trial status, so a trial can convert without ever being held.
          </span>
        </div>

        {/* Sections */}
        {[
          { title: 'Needs Scheduling', rows: needsScheduling, empty: 'No trial requests waiting on scheduling.' },
          { title: 'Upcoming', rows: upcoming, empty: 'No upcoming trials.' },
          { title: 'Needs Outcome', rows: needsOutcome, empty: 'No trials waiting on an outcome.' },
          { title: 'Recent', rows: recent, empty: 'No completed trials yet.' },
        ].map(section => (
          <div key={section.title} className="space-y-2">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
              {section.title}
              <span className="text-xs text-gray-400 font-normal normal-case">({section.rows.length})</span>
            </h2>
            {section.rows.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-6 text-center">
                <p className="text-sm text-gray-400 italic">{section.empty}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {section.rows.map(trial => (
                  <TrialRow
                    key={trial.id}
                    trial={trial}
                    siblingCount={trial.groupId ? groupCounts[trial.groupId] : 0}
                    onSchedule={t => setScheduleModal(t)}
                    onReschedule={t => setRescheduleModal(t)}
                    onOutcome={t => setOutcomeModal(t)}
                    onQuickCancel={handleQuickCancel}
                    onMarkExpired={handleMarkExpired}
                    onOverride={t => setOverrideModal(t)}
                    onEdit={t => { setSaveResult(null); setFormModal({ mode: 'edit', trial: t }); }}
                    onDelete={handleDelete}
                    onDeleteGroup={handleDeleteGroup}
                    onLinkAccount={handleLinkToAccount}
                    actionLoading={actionLoading}
                  />
                ))}
              </div>
            )}
          </div>
        ))}

      </div>

      {formModal && (
        <TrialFormModal
          trial={formModal.mode === 'edit' ? formModal.trial : null}
          teachers={teachers}
          referralCodes={referralCodes}
          trials={trials}
          onSave={handleSaveTrial}
          onClose={() => { setSaveResult(null); setFormModal(null); }}
          saving={saving}
          saveResult={saveResult}
        />
      )}

      {scheduleModal && (
        <ScheduleModal
          trial={scheduleModal}
          teachers={teachers}
          onConfirm={handleScheduleConfirm}
          onClose={() => setScheduleModal(null)}
          saving={saving}
        />
      )}

      {rescheduleModal && (
        <RescheduleModal
          trial={rescheduleModal}
          teachers={teachers}
          onConfirm={handleRescheduleConfirm}
          onClose={() => setRescheduleModal(null)}
          saving={saving}
        />
      )}

      {outcomeModal && (
        <OutcomeModal
          trial={outcomeModal}
          onConfirm={handleOutcomeConfirm}
          onClose={() => setOutcomeModal(null)}
          saving={saving}
        />
      )}

      {overrideModal && (
        <OverrideModal
          trial={overrideModal}
          onConfirm={handleOverrideConfirm}
          onClose={() => setOverrideModal(null)}
          saving={saving}
        />
      )}

      {deleteModal && (
        <DeleteTrialModal
          context={deleteModal}
          allTrials={trials}
          onConfirm={handleDeleteConfirm}
          onClose={() => setDeleteModal(null)}
          saving={saving}
        />
      )}

      {linkModal && (
        <LinkAccountModal
          context={linkModal}
          onConfirm={handleLinkConfirm}
          onClose={() => setLinkModal(null)}
          saving={saving}
        />
      )}
    </div>
  );
}

export default TrialManager;
